import { ChangeDetectionStrategy, Component, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { PageLayoutComponent, TitileNavbarComponent } from '@ui-components';
import { DaysRangeSliderComponent } from '../days-range-slider/days-range-slider.component';
import { AdminGamesStatsQueryResult, GetAdminGamesStatsQueryHandler } from '../../application/handlers';

enum ViewMode {
  BY_DAY = 'byDay',
  BY_CATEGORY = 'byCategory',
  BY_REGION = 'byRegion'
}

@Component({
  selector: 'lib-games-stats',
  templateUrl: './games-stats.component.html',
  styleUrls: ['./games-stats.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [DaysRangeSliderComponent, TitileNavbarComponent, PageLayoutComponent],
  providers: [GetAdminGamesStatsQueryHandler]
})
export class GamesStatsComponent {
  private readonly queryHandler: GetAdminGamesStatsQueryHandler = inject(GetAdminGamesStatsQueryHandler);

  readonly statsDays: WritableSignal<number> = signal<number>(7);
  readonly ViewMode: typeof ViewMode = ViewMode;
  readonly viewMode: WritableSignal<ViewMode> = signal<ViewMode>(ViewMode.BY_DAY);
  readonly expandedCategories: WritableSignal<Set<string>> = signal<Set<string>>(new Set());
  readonly expandedRegion: WritableSignal<string | null> = signal<string | null>(null);
  readonly expandedDay: WritableSignal<string | null> = signal<string | null>(null);
  readonly expandedCategoryItem: WritableSignal<string | null> = signal<string | null>(null);
  readonly regionTotalsOpen: WritableSignal<boolean> = signal<boolean>(false);

  readonly stats: Signal<AdminGamesStatsQueryResult | undefined> = toSignal(
    toObservable(this.statsDays).pipe(switchMap((days: number) => this.queryHandler.query({ statsDays: days })))
  );

  setPeriod(period: number): void {
    this.statsDays.set(period);
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode.set(mode);
    this.expandedRegion.set(null);
    this.expandedDay.set(null);
    this.expandedCategoryItem.set(null);
  }

  isCategoryOpen(label: string): boolean {
    return this.expandedCategories().has(label);
  }

  toggleCategory(label: string): void {
    this.expandedCategories.update((set: Set<string>) => {
      const next: Set<string> = new Set(set);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }

  toggleDay(rawDate: string): void {
    this.expandedDay.update((current: string | null) => (current === rawDate ? null : rawDate));
  }

  toggleCategoryItem(key: string): void {
    this.expandedCategoryItem.update((current: string | null) => (current === key ? null : key));
  }

  toggleRegionTotals(): void {
    this.regionTotalsOpen.update((v: boolean) => !v);
  }

  toggleRegion(regionName: string): void {
    this.expandedRegion.update((current: string | null) => (current === regionName ? null : regionName));
  }
}
